import React from "react";
import { motion } from "framer-motion";

/**
 * RevealText — splits a string into words and animates each one with a
 * stagger fade+slide-up reveal. Designed to mirror "Awwwards" style title
 * entrances without breaking the editorial typography of the rest of the
 * site (use it as a child of an existing <h2 className="display ...">).
 *
 * Each word also leaves whitespace alive so layout flows naturally.
 *
 *   <h2 className="display ...">
 *     <RevealText text="¡Solicita energía en" />{" "}
 *     <RevealText text="3 sencillos pasos!" className="display-accent" delay={0.18} />
 *   </h2>
 */
export default function RevealText({
  text,
  className = "",
  delay = 0,
  step = 0.06,
  yOffset = 24,
  inline = true,
}) {
  if (!text) return null;
  const words = String(text).split(" ");
  const Wrapper = inline ? "span" : "div";

  return (
    // aria-label exposes the full string to assistive tech *and* search-engine
    // crawlers as a single phrase. Children get aria-hidden so the animated
    // word spans aren't re-read.
    // Previously we had an extra `.sr-only` clone of the text below the
    // children — Google was indexing BOTH, producing duplicated phrases in
    // the SERP snippet ("Energía confiable, Energía confiable, hecha para ti
    // hecha para ti…"). Removed.
    <Wrapper className={`reveal-text ${className}`} aria-label={text}>
      {words.map((w, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="reveal-text-word"
          style={{
            display: "inline-block",
            verticalAlign: "baseline",
            // No overflow:hidden — clips italic ascenders/descenders.
            // Vertical slide already works via translate on the inner motion.span.
          }}
        >
          <motion.span
            initial={{ y: yOffset, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
              delay: delay + i * step,
            }}
            style={{ display: "inline-block", willChange: "transform, opacity" }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 && "\u00A0"}
        </span>
      ))}
    </Wrapper>
  );
}
